import React, { useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import {
  AddButtonUi,
  AutoCompleteSelectUI,
  ButtonUI,
  DatePickerUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import companyService from '../../service/company.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useConfig from '../../context/ConfigContext';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, DocStatusList } from '../../config/document';
import exchangeRateService from '../../service/exchangeRate.service';
import { IconButton } from '@mui/material';
import { DocumentType } from '../../config/DocumentType';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { FromType, PaymentDeadlineBasisType } from '../../config/paymentMethod';
import dayjs from 'dayjs';

const DocumentFields = ({
  viewField,
  autocompleteOptions,
  selectedValues,
  autocompleteData,
  documentData,
  selectedCompanyState,
  companyContactsState,
  setSettlementDialogOpen,
  docTypeId,
  getDownPaymentDoc,
  currentDocType,
  setCompanyDialogOpen,
  onCompanyViewOpen,
  companyBankRefreshKey,
  setBulkUpdateDialogState,
  setProjectDialogOpen,
  setCostCenterDialogOpen,
}) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedCompany, setSelectedCompany } = selectedCompanyState;
  const { setCompanyContacts } = companyContactsState;
  const isCopyMode = selectedValues?.mode === 'copy';
  const hasSelectedValues = Boolean(selectedValues && Object.keys(selectedValues).length > 0);
  const isLoadedExistingDoc = hasSelectedValues && !isCopyMode;
  const isLoadedCompanyDataForCurrentDoc =
    isLoadedExistingDoc &&
    Boolean(values?.docId) &&
    values?.companyId === selectedValues?.companyId &&
    selectedCompany?.id === selectedValues?.companyId;
  const initialLoadedKeyRef = useRef(null);
  const initialLoadSkippedRef = useRef(false);
  const lastHandledKeyRef = useRef(null);
  const lastHandledConfigCurrencyRef = useRef(null);
  const copyCurrencyCodeRef = useRef(null);
  const copyExchangeRateRef = useRef(null);
  const preservedCopyRateAppliedRef = useRef(false);
  const companyBankRequestIdRef = useRef(0);
  const latestCompanyBankIdRef = useRef(values.companyBankId);
  const [companyBankRows, setCompanyBankRows] = useState([]);
  const showCompanyFields = Boolean(currentDocType?.company || currentDocType?.cooperation);
  const companyLabel = currentDocType?.cooperation
    ? 'Partner'
    : documentData.direction === DocumentDirection.INCOMING
      ? 'Szállító'
      : 'Vevő';

  const isInventoryAccountingDoc = Boolean(documentData?.inventoryAccounting);
  const isNewDocument = !values?.docId && (!selectedValues || Object.keys(selectedValues).length === 0);
  const isDocDateLocked = isInventoryAccountingDoc && !isNewDocument && values?.docStatus !== DocStatus.PLANNED;
  const requiresExchangeRate = Boolean(config?.currencyCode && values?.currencyCode && values.currencyCode !== config.currencyCode);
  const selectedCustomStatusLabel =
    (!isCopyMode ? selectedValues?.customDocStatus?.name : '') ||
    autocompleteData?.customStatus?.find((status) => status.code === values.customStatus)?.name ||
    '';
  const selectedPriorityLabel =
    (!isCopyMode ? selectedValues?.documentPiority?.name : '') ||
    autocompleteData?.documentPiority?.find((priority) => priority.priorityId === values.priorityId)?.name ||
    '';
  const companyBankOptions = generateAutocompleteItems(
    companyBankRows,
    ['bankAccountNumber', 'iban', 'currencyCode'],
    'id'
  );
  const selectedCompanyBank = companyBankRows.find((bank) => bank.id === values.companyBankId);
  const selectedCompanyBankLabel =
    [selectedCompanyBank?.bankAccountNumber, selectedCompanyBank?.iban, selectedCompanyBank?.currencyCode]
      .filter(Boolean)
      .join(' - ') ||
    (values.companyBankId
      ? [
          selectedValues?.companyBank?.bankAccountNumber,
          selectedValues?.companyBank?.iban,
          selectedValues?.companyBank?.currencyCode,
        ]
          .filter(Boolean)
          .join(' - ')
      : '');
  const hidedFieldNames = JSON.parse(params.DOCUMENT_HIDE_FIELDS ?? '{}');

  const getCompanyData = async (companyId) => {
    const selCom = await companyService.getCompanyById(companyId, true);
    setSelectedCompany(selCom);
  };

  const getDailyRate = async () => {
    const rateFilter = { currencyCode: values.currencyCode, rateDate: values.docDate };
    setFieldValue('exchangeRate', '');
    const rate = (await exchangeRateService.getDailyRate(rateFilter)) ?? null;
    if (rate) {
      setFieldValue('exchangeRate', parseFloat(rate.rate));
    }
  };

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  latestCompanyBankIdRef.current = values.companyBankId;

  useEffect(() => {
    const requestId = ++companyBankRequestIdRef.current;
    let active = true;

    if (!currentDocType?.useBankAccount) {
      setCompanyBankRows([]);
      return () => {
        active = false;
      };
    }

    if (!values.companyId || !values.currencyCode) {
      setCompanyBankRows([]);
      latestCompanyBankIdRef.current = null;
      setFieldValue('companyBankId', null);
      return () => {
        active = false;
      };
    }

    companyService
      .getCompanyBanks(values.companyId, values.currencyCode)
      .then((data) => {
        if (!active || requestId !== companyBankRequestIdRef.current) return;

        const rows = data?.rows ?? [];
        const currentBankId = latestCompanyBankIdRef.current;
        const currentBankIsValid = rows.some((bank) => bank.id === currentBankId);
        const nextBankId = currentBankIsValid ? currentBankId : data?.defaultBankAccount?.id ?? null;

        setCompanyBankRows(rows);
        latestCompanyBankIdRef.current = nextBankId;
        if (nextBankId !== currentBankId) {
          setFieldValue('companyBankId', nextBankId);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [currentDocType?.useBankAccount, values.companyId, values.currencyCode, companyBankRefreshKey, setFieldValue]);

  useEffect(() => {
    if (!hasSelectedValues) {
      initialLoadedKeyRef.current = null;
      initialLoadSkippedRef.current = false;
      lastHandledKeyRef.current = null;
      lastHandledConfigCurrencyRef.current = null;
      copyCurrencyCodeRef.current = null;
      copyExchangeRateRef.current = null;
      preservedCopyRateAppliedRef.current = false;
      return;
    }

    initialLoadedKeyRef.current = `${selectedValues?.currencyCode ?? ''}|${
      selectedValues?.docDate ? dayjs(selectedValues.docDate).format('YYYY-MM-DD') : ''
    }`;
    initialLoadSkippedRef.current = false;
    lastHandledKeyRef.current = null;
    lastHandledConfigCurrencyRef.current = null;
    copyCurrencyCodeRef.current = selectedValues?.mode === 'copy' ? selectedValues?.currencyCode ?? null : null;
    copyExchangeRateRef.current = selectedValues?.mode === 'copy' ? selectedValues?.exchangeRate ?? null : null;
    preservedCopyRateAppliedRef.current = false;
  }, [hasSelectedValues, selectedValues?.docId]);

  useEffect(() => {
    if (values.companyId) {
      const matchedCompany =
        autocompleteData?.company?.find((i) => i.id === values.companyId) ??
        (selectedValues?.company?.id === values.companyId ? selectedValues.company : null);

      setFieldValue('companyCode', matchedCompany?.code ?? selectedValues?.companyCode ?? '');
      setFieldValue('companyName', matchedCompany?.name ?? selectedValues?.companyName ?? '');

      const hasLoadedCompanyDetails =
        selectedCompany?.id === values.companyId &&
        Array.isArray(selectedCompany?.companyAdress) &&
        Array.isArray(selectedCompany?.companyContact);

      if (!hasLoadedCompanyDetails) {
        getCompanyData(values.companyId);
      }
    } else {
      setSelectedCompany(null);
      setCompanyContacts([]);
    }
  }, [values.companyId, autocompleteData?.company, selectedValues?.company, selectedValues?.companyCode, selectedValues?.companyName]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;
    if (!selectedCompany) {
      if (values.companyId) return;

      const findCurrencyByDefPl = autocompleteData?.priceList?.find((p) => p.id === config.defaultPriceList);

      setFieldValue('companyCode', '');
      setFieldValue('companyName', '');
      setFieldValue('companyContactId', null);
      setFieldValue('companyBankId', null);
      setFieldValue('currencyCode', findCurrencyByDefPl?.currencyCode || config.currencyCode || null);
      setFieldValue('paymentMethodId', config.defPaymentMethod ?? null);
      setFieldValue('salesOrPurchasePersonId', null);
      setFieldValue('deliveryMethodId', null);
      setFieldValue('incotermCode', null);
      setFieldValue('priceListId', config.defaultPriceList ?? null);
      setFieldValue('langCode', null);
      setFieldValue('hunTaxNumber', '');
      setFieldValue('euTaxNumber', '');
      setFieldValue('foreignTaxNumber', '');
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', '');
      setFieldValue('incotermCity', '');
      setFieldValue('companyWarning', '');
      return;
    }

    if (selectedCompany.id !== values.companyId) return;

    setCompanyContacts(generateAutocompleteItems(selectedCompany.companyContact, 'name', 'id'));

    if (!isLoadedCompanyDataForCurrentDoc) {
      const preservedCurrencyCode = isCopyMode ? copyCurrencyCodeRef.current : null;
      const preservedExchangeRate = isCopyMode ? copyExchangeRateRef.current : null;
      const defContact = selectedCompany.companyContact.find((cc) => cc.default);
      setFieldValue('companyContactId', defContact?.id ?? selectedCompany?.companyContact[0]?.id ?? null);

      setFieldValue('langCode', selectedCompany.languageCode);
      setFieldValue('hunTaxNumber', selectedCompany.hunTaxNumber ?? '');
      setFieldValue('euTaxNumber', selectedCompany.euTaxNumber ?? '');
      setFieldValue('foreignTaxNumber', selectedCompany.foreignTaxNumber ?? '');
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', selectedCompany.shortName);
      setFieldValue('incotermCity', selectedCompany.incotermCity);
      setFieldValue('companyWarning', selectedCompany.companyWarning);

      if (documentData.direction === DocumentDirection.INCOMING) {
        const crncy = selectedCompany.supplierCurrencyCode || config.currencyCode;
        const payMethod = selectedCompany.supplierPaymentMethodId || config.defPaymentMethod;
        setFieldValue('currencyCode', preservedCurrencyCode ?? crncy);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
        setFieldValue('paymentMethodId', payMethod);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.purchasePersonId);
        setFieldValue('deliveryMethodId', selectedCompany.supplierDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.supplierIncotermCode);
        setFieldValue('priceListId', selectedCompany.supplierPriceListId);
      } else if (documentData.direction === DocumentDirection.OUTGOING) {
        const crncy = selectedCompany.customerCurrencyCode || config.currencyCode;
        const payMethod = selectedCompany.customerPaymentMethodId || config.defPaymentMethod;
        setFieldValue('currencyCode', preservedCurrencyCode ?? crncy);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
        setFieldValue('paymentMethodId', payMethod);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.salesPersonId);
        setFieldValue('deliveryMethodId', selectedCompany.customerDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.customerIncotermCode);
        setFieldValue('priceListId', selectedCompany.customerPriceListId);
      } else {
        setFieldValue('currencyCode', preservedCurrencyCode ?? config.currencyCode);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
      }
    }
  }, [selectedCompany, isCopyMode, values.companyId]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;

    const currentKey = `${values?.currencyCode ?? ''}|${values?.docDate ? dayjs(values.docDate).format('YYYY-MM-DD') : ''}`;
    const isInitialLoadedState = hasSelectedValues && currentKey === initialLoadedKeyRef.current;
    const hasPreservedCopyRate =
      isCopyMode &&
      copyExchangeRateRef.current !== null &&
      copyExchangeRateRef.current !== undefined &&
      currentKey === initialLoadedKeyRef.current;

    if (lastHandledKeyRef.current === currentKey && lastHandledConfigCurrencyRef.current === config.currencyCode) {
      return;
    }

    if (!initialLoadSkippedRef.current && isInitialLoadedState) {
      initialLoadSkippedRef.current = true;
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      return;
    }

    if (!requiresExchangeRate) {
      setFieldValue('exchangeRate', '');
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      preservedCopyRateAppliedRef.current = false;
      return;
    }

    if (hasPreservedCopyRate && !preservedCopyRateAppliedRef.current) {
      setFieldValue('exchangeRate', copyExchangeRateRef.current);
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      preservedCopyRateAppliedRef.current = true;
      return;
    }

    if (!values.docDate) {
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      setFieldValue('exchangeRate', '');

      if (isNewDocument) {
        return;
      }

      addNotification({
        content: 'Teljesítés dátuma nélkül nem tudjuk az árfolyamot betölteni!',
        type: NotificationType.ERROR,
      });
      return;
    }

    getDailyRate();

    lastHandledKeyRef.current = currentKey;
    lastHandledConfigCurrencyRef.current = config.currencyCode;
    preservedCopyRateAppliedRef.current = false;
  }, [values.currencyCode, values.docDate, config.currencyCode, hasSelectedValues, requiresExchangeRate, isNewDocument, isCopyMode]);

  useEffect(() => {
    if (values.projectId) {
      const findedProject = autocompleteData?.project.find((p) => p.projectId === values.projectId);
      handleFieldChange('projectOrderNumber', { value: findedProject?.orderNumber ?? '' });
    } else {
      setFieldValue('projectOrderNumber', '');
    }
  }, [values.projectId]);

  const handleFieldChange = async (field, newVal) => {
    // Normalize empty-string values to null to avoid wiping defaults
    let selected = newVal?.value ?? null;
    if (selected === '') selected = null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const isSameDayValue = (leftValue, rightValue) => {
    if (!leftValue && !rightValue) return true;
    if (!leftValue || !rightValue) return false;

    return dayjs(leftValue).isSame(dayjs(rightValue), 'day');
  };

  const handleBulkUpdatableFieldChange = async ({
    headerField,
    value,
    getLineFieldPath,
    getLineFieldValue,
    dialogTitle,
    dialogMessage,
    confirmButtonText = 'Sorok módosítása',
    areEqual = (leftValue, rightValue) => leftValue === rightValue,
  }) => {
    const previousValue = values?.[headerField] ?? null;

    await handleFieldChange(headerField, { value });

    const lines = Array.isArray(values.docLine) ? values.docLine : [];
    if (lines.length === 0 || areEqual(previousValue, value)) {
      return;
    }

    const hasDifferentLineValue = lines.some((line, index) => !areEqual(getLineFieldValue(line, index), value));
    if (!hasDifferentLineValue || typeof setBulkUpdateDialogState !== 'function') {
      return;
    }

    setBulkUpdateDialogState({
      open: true,
      title: dialogTitle,
      message: dialogMessage,
      confirmButtonText,
      value,
      fieldPaths: lines.map((_line, index) => getLineFieldPath(index)),
      setFieldValue,
      validateField,
    });
  };

  const handleProjectFieldChange = async (newVal) => {
    const projectId = newVal?.value ?? null;
    const previousProjectId = values?.projectId ?? null;
    const selectedProject = autocompleteData?.project?.find((project) => project.projectId === projectId) ?? null;
    const projectOrderNumber = selectedProject?.orderNumber ?? '';

    await handleFieldChange('projectId', { value: projectId });

    const lines = Array.isArray(values.docLine) ? values.docLine : [];
    if (lines.length === 0 || previousProjectId === projectId || typeof setBulkUpdateDialogState !== 'function') {
      return;
    }

    setBulkUpdateDialogState({
      open: true,
      title: 'Tetelsorok projektje',
      message: 'A fejléc projektje módosult. Frissítsük a tételsorok projekt mezőit is?',
      confirmButtonText: 'Sorok frissítése',
      updates: lines.flatMap((_line, index) => [
        {
          fieldPath: `docLine.${index}.projectId`,
          value: projectId,
          validatePath: `docLine.${index}.projectId`,
        },
        {
          fieldPath: `docLine.${index}.projectOrderNumber`,
          value: projectOrderNumber,
        },
      ]),
      setFieldValue,
      validateField,
    });
  };

  const handleCalcDueDate = async (issueDate, docDate, paymentMethodId) => {
    if (currentDocType?.disableDatePrefill || !paymentMethodId || !currentDocType?.invoice) return;

    const paymentMethod = autocompleteData.paymentMethod.find((method) => method.id === paymentMethodId);
    if (!paymentMethod) return;

    const defDate =
      paymentMethod.paymentDeadlineBasis === PaymentDeadlineBasisType.DATEOFCOMPLETION ? docDate : issueDate;

    if (!defDate) return;

    const calcDate =
      paymentMethod.from === FromType.MONTHEND ? dayjs(defDate).add(1, 'month').startOf('month') : dayjs(defDate);

    const day = Number(paymentMethod.day ?? 0);
    const dueDate = paymentMethod.from === FromType.MONTHEND ? calcDate.add(day - 1, 'day') : calcDate.add(day, 'day');

    await handleBulkUpdatableFieldChange({
      headerField: 'dueDate',
      value: dueDate,
      getLineFieldPath: (index) => `docLine.${index}.dueDate`,
      getLineFieldValue: (line) => line?.dueDate ?? null,
      dialogTitle: 'Tételsorok határideje',
      dialogMessage: 'A fejléci határidő módosult. Frissítsük a tételsorok határidejét is?',
      confirmButtonText: 'Sorok frissítése',
      areEqual: isSameDayValue,
    });
  };

  const skipField = (fieldName) => (hidedFieldNames[docTypeId] || []).includes(fieldName);
  const selectedCompanyId = values.companyId ?? selectedValues?.company?.id ?? null;

  return (
    <>
      <div className="grid grid-cols-6 gap-x-4 gap-y-4 mx-4 mb-2">
        {!skipField('docNum') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="docNum"
              as={InputUI}
              fullWidth
              disabled={viewField || !currentDocType?.manualDocNum || isLoadedExistingDoc}
              label={<FormLabelUI text="Bizonylatszám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docNum} />}
              InputLabelProps={{ shrink: Boolean(values.docNum) }}
            />
          </div>
        )}

        {!skipField('docStatus') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="docStatus"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Státusz" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docStatus} />}
              InputLabelProps={{ shrink: Boolean(values.docStatus) }}
              onChange={(_e, newVal) => handleFieldChange('docStatus', newVal)}
              selectedValue={values.docStatus}
              selectedLabelValue={DocStatusList?.find((p) => p.value === values.docStatus)?.label}
              dataset={DocStatusList}
            />
          </div>
        )}

        {!skipField('customStatus') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="customStatus"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Egyedi státusz" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.customStatus} />}
              InputLabelProps={{ shrink: Boolean(values.customStatus) }}
              onChange={(_e, newVal) => handleFieldChange('customStatus', newVal)}
              selectedValue={values.customStatus}
              selectedLabelValue={selectedCustomStatusLabel}
              dataset={autocompleteOptions?.customStatus}
            />
          </div>
        )}

        {currentDocType?.useDocumentPriority && !skipField('priorityId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="priorityId"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Prioritás" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.priorityId} />}
              InputLabelProps={{ shrink: Boolean(values.priorityId) }}
              onChange={(_event, newValue) => handleFieldChange('priorityId', newValue)}
              selectedValue={values.priorityId}
              selectedLabelValue={selectedPriorityLabel}
              dataset={autocompleteOptions?.documentPiority}
            />
          </div>
        )}

        {!skipField('docDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="docDate"
              component={DatePickerUI}
              value={values.docDate}
              fullWidth
              label={<FormLabelUI text="Teljesítés dátuma" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docDate} />}
              onChange={(date) => {
                handleFieldChange('docDate', { value: date });
                handleCalcDueDate(values.issueDate, date, values.paymentMethodId);
              }}
              disabled={viewField || isDocDateLocked}
              InputLabelProps={{ shrink: Boolean(values.docDate) }}
            />
          </div>
        )}

        {!skipField('issueDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="issueDate"
              component={DatePickerUI}
              value={values.issueDate}
              fullWidth
              label={<FormLabelUI text="Kiállítás dátuma" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.issueDate} />}
              onChange={(date) => {
                handleFieldChange('issueDate', { value: date });
                handleCalcDueDate(date, values.docDate, values.paymentMethodId);
              }}
              disabled={viewField}
              InputLabelProps={{ shrink: Boolean(values.issueDate) }}
            />
          </div>
        )}

        {!skipField('dueDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="dueDate"
              component={DatePickerUI}
              value={values.dueDate}
              fullWidth
              label={<FormLabelUI text="Határidő" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.dueDate} />}
              onChange={(date) =>
                handleBulkUpdatableFieldChange({
                  headerField: 'dueDate',
                  value: date,
                  getLineFieldPath: (index) => `docLine.${index}.dueDate`,
                  getLineFieldValue: (line) => line?.dueDate ?? null,
                  dialogTitle: 'Tételsorok határideje',
                  dialogMessage: 'A fejléci határidő módosult. Frissítsük a tételsorok határidejét is?',
                  confirmButtonText: 'Sorok frissítése',
                  areEqual: isSameDayValue,
                })
              }
              disabled={viewField}
              InputLabelProps={{ shrink: Boolean(values.dueDate) }}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        {showCompanyFields && (
          <>
            {!skipField('companyId') && (
              <div className="col-span-4 md:col-span-1">
                <div className="flex items-center">
                  <Field
                    type="autocomplete"
                    name="companyId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text={companyLabel} />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.companyId} />}
                    onChange={async (_e, newVal) => handleFieldChange('companyId', newVal)}
                    disabled={viewField}
                    InputLabelProps={{ shrink: Boolean(values.companyId) }}
                    selectedValue={values.companyId}
                    selectedLabelValue={
                      selectedValues?.company?.name ||
                      autocompleteData.company.find((cm) => cm.id === values.companyId)?.name
                    }
                  dataset={autocompleteOptions?.company}
                  />
                  {selectedCompanyId && (
                    <IconButton
                      size="small"
                      sx={{
                        color: '#0076f8',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCompanyViewOpen?.(selectedCompanyId);
                      }}
                    >
                      <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  <AddButtonUi onClick={() => setCompanyDialogOpen(true)} disabled={viewField} />
                </div>
              </div>
            )}

            {!skipField('companyCode') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="text"
                  name="companyCode"
                  as={InputUI}
                  fullWidth
                  disabled={true}
                  label={<FormLabelUI text="Partner kód" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyCode} />}
                  InputLabelProps={{ shrink: Boolean(values.companyCode) }}
                />
              </div>
            )}

            {!skipField('companyName') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="text"
                  name="companyName"
                  as={InputUI}
                  fullWidth
                  disabled={true}
                  label={<FormLabelUI text="Partner név" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyName} />}
                  InputLabelProps={{ shrink: Boolean(values.companyName) }}
                />
              </div>
            )}

            {currentDocType?.useBankAccount && !skipField('companyBankId') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="autocomplete"
                  name="companyBankId"
                  component={AutoCompleteSelectUI}
                  fullWidth
                  label={<FormLabelUI text="Bankszámla" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyBankId} />}
                  onChange={(_e, newVal) => handleFieldChange('companyBankId', newVal)}
                  disabled={viewField || !values.companyId}
                  InputLabelProps={{ shrink: Boolean(values.companyBankId) }}
                  selectedValue={values.companyBankId}
                  selectedLabelValue={selectedCompanyBankLabel}
                  dataset={companyBankOptions}
                />
              </div>
            )}
          </>
        )}

        {!skipField('reference') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="reference"
              as={InputUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Hivatkozási szám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.reference} />}
              InputLabelProps={{ shrink: Boolean(values.reference) }}
            />
          </div>
        )}

        {currentDocType?.employee && !skipField('employeeId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="employeeId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Dolgozó" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.employeeId} />}
              onChange={(_e, newVal) => handleFieldChange('employeeId', newVal)}
              disabled={viewField}
              InputLabelProps={{ shrink: Boolean(values.employeeId) }}
              selectedValue={values.employeeId}
              selectedLabelValue={
                selectedValues?.employee?.name ||
                autocompleteData?.employee?.find((employee) => employee.id === values.employeeId)?.name
              }
              dataset={autocompleteOptions?.employee}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        {!skipField('paymentMethodId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="paymentMethodId"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Fizetési mód" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.paymentMethodId} />}
              InputLabelProps={{ shrink: Boolean(values.paymentMethodId) }}
              onChange={(_e, newVal) => {
                handleFieldChange('paymentMethodId', newVal);
                handleCalcDueDate(values.issueDate, values.docDate, newVal?.value ?? null);
              }}
              selectedValue={values.paymentMethodId}
              selectedLabelValue={selectedValues?.paymentMethod?.name}
              dataset={autocompleteOptions?.paymentMethod}
            />
          </div>
        )}

        {currentDocType?.project && (
          <>
            {!skipField('projectId') && (
              <div className="col-span-4 md:col-span-1">
                <div className="flex items-center">
                  <Field
                    type="autocomplete"
                    name="projectId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Projekt" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.projectId} />}
                    onChange={(_e, newVal) => handleProjectFieldChange(newVal)}
                    disabled={viewField}
                    InputLabelProps={{ shrink: Boolean(values.projectId) }}
                    selectedValue={values.projectId}
                    selectedLabelValue={selectedValues?.project?.projectName}
                    dataset={autocompleteOptions?.project}
                  />
                  <AddButtonUi onClick={() => setProjectDialogOpen(true)} disabled={viewField} />
                </div>
              </div>
            )}

            {!skipField('projectOrderNumber') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="autocomplete"
                  name="projectOrderNumber"
                  component={InputUI}
                  fullWidth
                  label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.projectOrderNumber} />}
                  disabled={true}
                  InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
                  value={values.projectOrderNumber}
                />
              </div>
            )}
          </>
        )}

        {!skipField('costCenterCode') && (
          <div className="col-span-4 md:col-span-1">
            <div className="flex items-center">
              <Field
                type="autocomplete"
                name="costCenterCode"
                component={AutoCompleteSelectUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Költséghely" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.costCenterCode} />}
                InputLabelProps={{ shrink: Boolean(values.costCenterCode) }}
                onChange={(_e, newVal) => handleFieldChange('costCenterCode', newVal)}
                selectedValue={values.costCenterCode}
                selectedLabelValue={selectedValues?.costCenter?.name}
                dataset={autocompleteOptions?.costCenter}
              />
              <AddButtonUi onClick={() => setCostCenterDialogOpen(true)} disabled={viewField} />
            </div>
          </div>
        )}

        {!skipField('currencyCode') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="currencyCode"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Pénznem" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.currencyCode} />}
              InputLabelProps={{ shrink: Boolean(values.currencyCode) }}
              selectedValue={values.currencyCode}
              selectedLabelValue={autocompleteData?.currency?.find((p) => p.code === values.currencyCode)?.code}
              onChange={(_e, newVal) => handleFieldChange('currencyCode', newVal)}
              dataset={autocompleteOptions?.currency}
            />
          </div>
        )}

        {requiresExchangeRate && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="number"
              name="exchangeRate"
              as={InputUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Árfolyam" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.exchangeRate} />}
              InputLabelProps={{ shrink: Boolean(values.exchangeRate) }}
            />
          </div>
        )}

        {showCompanyFields && [DocumentType.ARInvoice, DocumentType.APInvoice].includes(parseInt(docTypeId)) && (
            <div className="pt-3">
              <ButtonUI
                text="Előleg"
                className="bg-primary"
                size="sm"
                disabled={!values.companyId || viewField}
                onClick={() => getDownPaymentDoc(values.companyId)}
              />
            </div>
          )}

        {documentData.enableSettlement && (
          <div className="col-span-4 md:col-span-2">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Field
                  type="number"
                  name="openAmount"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Kintlévőség" />}
                  variant="standard"
                  disabled={true}
                  InputLabelProps={{ shrink: Boolean(values.openAmount) }}
                />
              </div>
              <IconButton
                onClick={() => {
                  if (isCopyMode) return;
                  setSettlementDialogOpen(true);
                }}
                disabled={isCopyMode || (!viewField && !Object.keys(selectedValues).length)}
              >
                <DescriptionIcon />
              </IconButton>
            </div>
          </div>
        )}
        {documentData.additionalCost && (
          <>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="number"
                name="extraCostTotal"
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség saját pénznemben" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostTotal} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostTotal) }}
              />
            </div>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="text"
                name="extraCostNote "
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség leírás" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostNote} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostNote) }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DocumentFields;
